import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, Square, Music, VolumeX, MessageCircle } from "lucide-react";
import { ELEMENTS } from "@/lib/elements";
import client from "@/lib/api";
import { useLang } from "@/lib/lang";
import { speak, stopSpeaking, isSpeechSupported } from "@/lib/voice";

const ElementPicker = ({ current, onPick, lang }) => (
  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="guantrai-element-picker">
    {ELEMENTS.map((e) => {
      const active = current.id === e.id;
      const { name, line } = e[lang] || e.fr;
      return (
        <button key={e.id} type="button" onClick={() => onPick(e)} data-testid={`guantrai-element-${e.id}`}
                aria-pressed={active}
                className="rounded-2xl p-4 text-left transition-[transform,border-color,background-color] duration-300 hover:-translate-y-1"
                style={{ background: active ? `${e.accent}22` : "rgba(255,255,255,0.03)", border: `1px solid ${active ? e.accent : "rgba(255,255,255,0.1)"}` }}>
          <e.Icon size={22} style={{ color: e.accent }} />
          <div className="mt-3 font-serif text-lg" style={{ color: active ? e.accent : "#eef2ff" }}>{name}</div>
          <div className="mt-1 text-[11px] leading-snug" style={{ color: "rgba(238,242,255,0.6)" }}>{line}</div>
        </button>
      );
    })}
  </div>
);

export default function Guantrai() {
  const { lang, setLang } = useLang();
  const [el, setEl] = useState(ELEMENTS[0]);
  const [poems, setPoems] = useState([]);
  const [speakingId, setSpeakingId] = useState(null);
  const [musicOn, setMusicOn] = useState(false);
  const audioRef = useRef(null);
  const voiceSupported = isSpeechSupported();

  useEffect(() => {
    client.get("/guantrai/poems", { params: { lang, element: el.id } })
      .then((r) => setPoems(r.data?.poems || []))
      .catch(() => setPoems([]));
  }, [lang, el.id]);

  useEffect(() => () => stopSpeaking(), []);

  useEffect(() => {
    const a = audioRef.current;
    if (a && musicOn) { a.volume = 0.3; a.play().catch(() => setMusicOn(false)); }
  }, [el.id, musicOn]);

  const toggleMusic = () => {
    const a = audioRef.current;
    if (!a) return;
    if (musicOn) { a.pause(); setMusicOn(false); } else { setMusicOn(true); }
  };

  const togglePoem = (id, text) => {
    stopSpeaking();
    if (speakingId === id) return setSpeakingId(null);
    setSpeakingId(id);
    speak(text, { lang, onEnd: () => setSpeakingId(null) });
  };

  const elName = (el[lang] || el.fr).name;

  return (
    <div data-testid="guantrai-page" className="min-h-screen relative"
         style={{ background: `radial-gradient(ellipse at top, ${el.accent}26 0%, ${el.bg} 45%, #020207 100%)`, color: "#eef2ff", transition: "background-color 600ms ease" }}>
      <audio ref={audioRef} src={el.playlist[0].url} loop preload="none" />
      <header className="relative z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" data-testid="guantrai-back" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: el.accent }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden" style={{ border: `1px solid ${el.accent}55` }}>
              {["fr", "en"].map((l) => (
                <button key={l} onClick={() => setLang(l)} type="button" data-testid={`guantrai-lang-${l}`} className="px-2.5 py-1.5 uppercase"
                        style={{ background: lang === l ? el.accent : "transparent", color: lang === l ? "#05060c" : "#eef2ff", fontWeight: lang === l ? 600 : 400 }}>{l}</button>
              ))}
            </div>
            <button type="button" onClick={toggleMusic} data-testid="guantrai-music-toggle"
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.14em]"
                    style={{ background: musicOn ? `${el.accent}33` : "rgba(255,255,255,0.05)", border: `1px solid ${el.accent}77`, color: el.accent }}>
              {musicOn ? <VolumeX size={13} /> : <Music size={13} />}
              {musicOn ? (lang === "fr" ? "Couper" : "Mute") : (lang === "fr" ? `Musique · ${elName}` : `Music · ${elName}`)}
            </button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="overline mb-4" style={{ color: el.accent }}><span className="kicker-line" />
          Guantraí · {lang === "fr" ? "les éléments apaisent le chagrin · gratuit illimité" : "the elements soothe sorrow · free unlimited"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04]">
          {lang === "fr" ? <>Quand le chagrin pèse, <span className="italic" style={{ color: el.accent }}>les éléments</span> te parlent.</>
            : <>When sorrow weighs, <span className="italic" style={{ color: el.accent }}>the elements</span> speak to you.</>}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "rgba(238,242,255,0.75)" }}>
          {lang === "fr"
            ? "Ici, ni sage ni morale. L'Eau, l'Air, le Feu, la Terre, l'Éther, le Végétal et le Minéral te disent seulement ce qu'ils savent depuis l'origine du monde — et ce que tes larmes, ton souffle ou ta brûlure sont vraiment. Choisis un élément, lis, écoute — ou parle-lui."
            : "No sage, no moral here. Water, Air, Fire, Earth, Ether, the Plant and the Mineral kingdoms simply tell you what they have known since the world began — and what your tears, your breath or your burning truly are. Choose an element, read, listen — or talk to it."}
        </p>
        <div className="mt-8"><ElementPicker current={el} onPick={setEl} lang={lang} /></div>
        <div className="mt-6 flex flex-wrap items-center gap-4">
          <Link to={el.href} data-testid={`guantrai-chat-link-${el.id}`}
                className="inline-flex items-center gap-3 pl-1.5 pr-5 py-1.5 rounded-full transition-transform duration-300 hover:-translate-y-0.5"
                style={{ background: `linear-gradient(135deg, ${el.accent}, ${el.accent}aa)`, color: "#05060c", fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(5,6,12,0.25)" }}><el.Icon size={16} /></span>
            <MessageCircle size={14} />
            {lang === "fr" ? `Parler avec ${elName.toLowerCase()}` : `Talk with ${elName}`}
          </Link>
          <Link to="/elements" data-testid="guantrai-elements-hub-link" className="text-xs underline" style={{ color: el.accent }}>
            {lang === "fr" ? "Tous les éléments" : "All elements"}
          </Link>
          <span className="text-xs" style={{ color: "rgba(238,242,255,0.6)" }}>
            {lang === "fr" ? "Chat gratuit et illimité" : "Free unlimited chat"}
          </span>
        </div>
      </section>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pb-24">
        <div className="grid gap-5 md:grid-cols-2" data-testid="guantrai-poem-list">
          {poems.map((p, i) => (
            <article key={p.id} data-testid={`guantrai-poem-${p.id}`} className="rounded-2xl p-6 fade-up"
                     style={{ animationDelay: `${i * 80}ms`, background: "rgba(5,8,18,0.6)", border: `1px solid ${el.accent}40`, backdropFilter: "blur(14px)" }}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="font-serif text-xl" style={{ color: el.accent }}>{p.title}</h3>
                  <p className="text-[11px] font-mono uppercase tracking-[0.2em] mt-1" style={{ color: "rgba(238,242,255,0.55)" }}>{p.subtitle}</p>
                </div>
                {voiceSupported && (
                  <button type="button" onClick={() => togglePoem(p.id, p.text)} data-testid={`guantrai-listen-${p.id}`}
                          aria-label={lang === "fr" ? "Écouter" : "Listen"}
                          className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full"
                          style={{ background: speakingId === p.id ? el.accent : `${el.accent}22`, border: `1px solid ${el.accent}88`, color: speakingId === p.id ? "#05060c" : el.accent }}>
                    {speakingId === p.id ? <Square size={14} /> : <Play size={14} />}
                  </button>
                )}
              </div>
              <pre className="whitespace-pre-wrap text-[14.5px] leading-[1.8]" style={{ color: "rgba(238,242,255,0.88)", fontFamily: "'EB Garamond', Georgia, serif" }}>{p.text}</pre>
            </article>
          ))}
        </div>
        {poems.length === 0 && (
          <div className="text-center py-16" data-testid="guantrai-empty" style={{ color: "rgba(238,242,255,0.5)" }}>
            {lang === "fr" ? "L'élément rassemble ses mots…" : "The element gathers its words…"}
          </div>
        )}
      </section>
    </div>
  );
}
